// Dependencies
import { useEffect, useRef, useCallback } from "react";
import { useImmer } from "use-immer";

/**
 * This hook is used to manage the state of a form. It allows you to set the
 * initial state, update the state, and reset the state.
 *
 * The purpose is to provide a simple way to manage the state of a form but
 * it doesn't necessarily need to be used for forms. It's flexible enough to
 * be used for any state management.
 */
const useForm = (props) => {
	const { schema, defaultValues } = props;

	const stateHasChanged = useRef(false);
	const receivedInitialState = useRef(defaultValues !== undefined);

	// hold both states in same immer object
	const [state, updateState] = useImmer({
		initial: defaultValues ? defaultValues : {},
		staging: defaultValues ? defaultValues : {},
	});

	// recomputes if the state has changed. This is used to determine if the
	// user should be warned about unsaved changes.
	useEffect(() => {
		const changed = JSON.stringify(state.initial) !== JSON.stringify(state.staging);

		if (changed && !stateHasChanged.current) {
			stateHasChanged.current = true;
		}
		if (!changed && stateHasChanged.current) {
			stateHasChanged.current = false;
		}
	}, [state.initial, state.staging]);

	/**
	 * Sets the initial state. This happens only once. The purpose is to
	 * replace the useEffect hook with an exhaustive dependency list. For
	 * example:
	 *
	 * useEffect(() => {
	 *     setState({...})
	 * }, [data.name, data.description, date.start, date.end])
	 *
	 * Can now just be replaced with:
	 *
	 * useEffect(() => {
	 *    setInitialState({...})
	 * })
	 */
	const setInitialState = (initialState) => {
		if (receivedInitialState.current) {
			return;
		}

		updateState((draft) => {
			draft.initial = initialState;
			draft.staging = initialState;
		});

		receivedInitialState.current = true;
	};

	/**
	 * Resets the states (initial and stage) to the new values.
	 */
	const setNewDefaultValues = (newDefaultValues) => {
		updateState((draft) => {
			draft.initial = newDefaultValues;
			draft.staging = newDefaultValues;
		});

		receivedInitialState.current = true;
	};

	/**
	 * Sets the staging state equal to the initial state.
	 */
	const resetForm = useCallback(() => {
		updateState((draft) => {
			draft.staging = draft.initial;
		});
	}, [updateState]);

	/**
	 * Updates the staging state.
	 */
	const updateForm = useCallback((newValues) => {
		updateState((draft) => {
			draft.staging = { ...draft.staging, ...newValues };
		});
	}, [updateState]);

	/**
	 * Computes if there are any changes between the initial and staging
	 * states.
	 *
	 * TODO - this is not very efficient. We should be able to compare the
	 * initial and staging states without stringifying them.
	 */
	const hasunsavedChanges = () => {
		return stateHasChanged.current;
	};

	/**
	 * Uses the zod schema to validate the staging state. if the state is
	 * invalid, it returns an array of errors that include the key path and
	 * the error message generated by zod.
	 */
	const validate = () => {
		const result = schema.safeParse(state.staging);

		let errors = {};

		if (result.success === false) {
			errors = result.error.errors.reduce((acc, issue) => {
				const error = {
					keyPath: issue.path,
					message: issue.message,
				};

				const key = error.keyPath.join(".");
				acc[key] = error;
				return acc;
			}, {});
		}

		return {
			isValid: result.success,
			errors: errors,
		};
	};

	return {
		validate,
		resetForm,
		updateForm,
		setInitialState,
		hasunsavedChanges,
		setNewDefaultValues,
		state: state.staging,
		initialState: state.initial,
		receivedInitialState: receivedInitialState.current,
	};
};

export default useForm;
